class FmFloatingWidgetHub extends HTMLElement {
  connectedCallback() {
    this._toggle = this.querySelector('.fm-floating-hub__toggle');
    this._hasSupport = this.dataset.integrationSupport === 'true';

    this._onInternalClick = this._handleInternalClick.bind(this);
    this._onDocumentClick = this._handleDocumentClick.bind(this);
    this._onKeydown = this._handleKeydown.bind(this);
    this._onZendeskClose = this._handleZendeskClose.bind(this);

    this.addEventListener('click', this._onInternalClick);
    document.addEventListener('keydown', this._onKeydown);

    this._applyBodyState();

    if (this._hasSupport) {
      this._pollZendesk();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onInternalClick);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeydown);
    this._unbindZendeskListeners();
    this._removeBodyState();
    clearInterval(this._zePoll);
    clearInterval(this._omnisendPoll);
  }

  _applyBodyState() {
    const body = document.body;

    body.classList.add('fm-floating-hub-ready');

    if (this.dataset.integrationSupport === 'true') {
      body.classList.add('fm-floating-hub-hide-support');
    }

    if (this.dataset.integrationPoints === 'true') {
      body.classList.add('fm-floating-hub-hide-points');
    }

    if (this.dataset.integrationOffer === 'true') {
      body.classList.add('fm-floating-hub-hide-offer');
    }
  }

  _removeBodyState() {
    document.body.classList.remove(
      'fm-floating-hub-ready',
      'fm-floating-hub-hide-support',
      'fm-floating-hub-hide-points',
      'fm-floating-hub-hide-offer'
    );
  }

  _setOpen(isOpen) {
    if (this.classList.contains('is-open') === isOpen) return;

    this.classList.toggle('is-open', isOpen);
    this._toggle?.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      document.addEventListener('click', this._onDocumentClick);
    } else {
      document.removeEventListener('click', this._onDocumentClick);
    }
  }

  _setSupportOpen(isOpen) {
    if (this.classList.contains('is-support-open') === isOpen) return;

    this.classList.toggle('is-support-open', isOpen);
    this._toggle?.setAttribute('aria-label', isOpen ? 'Close live chat' : 'Open help menu');
  }

  _pollZendesk() {
    if (typeof window.zE === 'function') {
      this._hideZendeskLauncher();
      this._bindZendeskListeners();
      return;
    }

    let attempts = 20;
    this._zePoll = setInterval(() => {
      if (typeof window.zE === 'function') {
        clearInterval(this._zePoll);
        this._hideZendeskLauncher();
        this._bindZendeskListeners();
      } else if (--attempts <= 0) {
        clearInterval(this._zePoll);
      }
    }, 300);
  }

  _bindZendeskListeners() {
    if (this._zendeskListenersBound || typeof window.zE !== 'function') return;

    for (const api of ['messenger', 'webWidget']) {
      try {
        window.zE(`${api}:on`, 'close', this._onZendeskClose);
        this._zendeskApi = api;
        this._zendeskListenersBound = true;
        return;
      } catch {}
    }
  }

  _unbindZendeskListeners() {
    if (!this._zendeskListenersBound || !this._zendeskApi || typeof window.zE !== 'function') return;

    try {
      window.zE(`${this._zendeskApi}:off`, 'close', this._onZendeskClose);
    } catch {}
  }

  _handleZendeskClose() {
    this._setSupportOpen(false);
    this._setOpen(false);
  }

  _callZendesk(method, ...args) {
    if (typeof window.zE !== 'function') return false;

    for (const api of ['messenger', 'webWidget']) {
      try {
        window.zE(api, method, ...args);
        return api;
      } catch {}
    }

    return false;
  }

  _hideZendeskLauncher() {
    this._callZendesk('hide');
  }

  _openZendesk() {
    const tryOpen = () => {
      const api = this._callZendesk('show');
      if (!api) return false;

      try {
        window.zE(api, 'open');
        this._setSupportOpen(true);
        return true;
      } catch {
        return false;
      }
    };

    if (!tryOpen()) {
      let attempts = 20;
      const poll = setInterval(() => {
        if (tryOpen() || --attempts <= 0) clearInterval(poll);
      }, 300);
    }
  }

  _closeZendesk() {
    const api = this._callZendesk('close');
    if (api) {
      try { window.zE(api, 'hide'); } catch {}
    }
    this._setSupportOpen(false);
  }

  _openOmnisend() {
    if (this._clickOmnisendTeaser()) return;

    let attempts = 20;
    this._omnisendPoll = setInterval(() => {
      if (this._clickOmnisendTeaser() || --attempts <= 0) clearInterval(this._omnisendPoll);
    }, 300);
  }

  _clickOmnisendTeaser() {
    const button = document.querySelector('[id^="omnisend-form-"][id$="-teaser-btn"]');
    if (!button || button.id.endsWith('-teaser-close-btn')) return false;

    const rect = button.getBoundingClientRect();
    const init = {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2
    };
    const previous = button.style.pointerEvents;
    button.style.pointerEvents = 'auto';

    try {
      button.dispatchEvent(new PointerEvent('pointerdown', init));
      button.dispatchEvent(new MouseEvent('mousedown', init));
      button.dispatchEvent(new PointerEvent('pointerup', init));
      button.dispatchEvent(new MouseEvent('mouseup', init));
      button.click();
    } catch {
      button.click();
    }

    button.style.pointerEvents = previous;
    return true;
  }

  _openSmile() {
    if (typeof window.SmileUI?.openPanel === 'function') {
      window.SmileUI.openPanel();
      return;
    }

    window.location.hash = 'smile-home';
    setTimeout(() => window.SmileUI?.openPanel?.(), 300);
  }

  _handleInternalClick(event) {
    if (event.target.closest('.fm-floating-hub__toggle')) {
      if (this.classList.contains('is-support-open')) {
        this._closeZendesk();
        this._setOpen(false);
      } else {
        this._setOpen(!this.classList.contains('is-open'));
      }
      return;
    }

    const action = event.target.closest('[data-fm-hub-action]');
    if (!action) return;

    this._setOpen(false);

    switch (action.dataset.fmHubAction) {
      case 'support': this._openZendesk(); break;
      case 'points': this._openSmile(); break;
      case 'offer': this._openOmnisend(); break;
    }
  }

  _handleDocumentClick(event) {
    if (!this.contains(event.target)) this._setOpen(false);
  }

  _handleKeydown(event) {
    if (event.key === 'Escape') {
      this._setOpen(false);
      if (this._hasSupport) this._closeZendesk();
    }
  }
}

if (!customElements.get('fm-floating-widget-hub')) {
  customElements.define('fm-floating-widget-hub', FmFloatingWidgetHub);
}
